/**
 * Dates and ids, in the brand's mono data voice.
 *
 * Everything the backend emits is UTC ISO-8601. Rendered in the reader's own
 * zone, because an operator deciding whether an account has been waiting since
 * yesterday should not have to convert, and with the absolute instant kept in a
 * `title` so the exact value is never lost.
 */

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
}

/**
 * "08:50" — the clock time an instant falls on, in the reader's own zone.
 *
 * For History's Journal view, where every row is dated by its `session_date`
 * and the hour is what distinguishes one morning from another. The instant is
 * `acquisition_started_at`, which `contracts/recording.py` documents as the
 * recorder's `start_wallclock_iso` converted to UTC at ingest and says is
 * meant to be read "in the caller's own timezone" — the same call Home's
 * "you usually record around 08:40" makes.
 *
 * **24-hour, explicitly (`hourCycle: 'h23'`).** Not the locale's own cycle,
 * which `formatDateTime` above takes: the journal's own `woke_at_local` is
 * stored and shown as a bare `"HH:MM"` (`readClockTime`), and a cell reading
 * "woke 06:40" beside a recording stamped "8:50 AM" would put two clocks in
 * one sentence.
 *
 * A dash for an absent or unparseable instant, as every formatter here does:
 * every row written before Sprint 1.9.9 carries a null, and the time lives
 * only inside that recording's frozen archive.
 */
export function formatClockTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
}

/** `YYYY-MM-DD`, and nothing else. */
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * A recording's `session_date`, which is a CALENDAR DATE and not an instant.
 *
 * ── WHY `formatDate` IS WRONG FOR IT ────────────────────────────────────────
 *
 * `new Date("2026-06-24")` is specified to parse a date-only string as **UTC
 * midnight**, and `toLocaleDateString` then renders that instant in the reader's
 * own zone. For anyone west of UTC — the Americas, all day — that is **23 June**.
 * The recording is dated by the day it was made, in the place it was made; there
 * is no instant to convert and converting one invents an off-by-one.
 *
 * This is not hypothetical and not new: `pages/admin/UserDetail.tsx` has rendered
 * `session_date` through `formatDate` since Sprint 1.8, and an operator in
 * New York has been reading every recording as the previous day. Sprint 1.9 puts
 * the same value on Today, History, the detail view and the trend chart, which is
 * how it was found.
 *
 * Anything that is not a bare `YYYY-MM-DD` falls through to `formatDate`, so a
 * caller that hands this a real timestamp is not silently mis-handled.
 */
export function formatSessionDate(date: string | null | undefined): string {
  if (!date) return '—'
  const parts = calendarDateParts(date)
  if (parts === null) return formatDate(date)
  // Local midnight, so the calendar day is the one the string names, in every
  // zone. The instant is meaningless here and is never shown.
  const d = new Date(parts.year, parts.month - 1, parts.day)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
}

/**
 * The name of the period a group STARTS in: `"21 Sep"` or `"October"`.
 *
 * For the Journal view's "Show the 6 days of 21 Sep" (Sprint 1.9.11 J2c,
 * concept §B2/§B3). It exists because the alternative is worse in both
 * directions: `journal-summary` sends the group's label ("Week of 21 Sep",
 * "October 2026") and a client that sliced `"Week of "` off it to make that
 * sentence would be PARSING a server-authored string, while using the label
 * whole reads "Show the 6 days of Week of 21 Sep".
 *
 * So the period's own `start_date` is formatted instead, which is the same
 * value the label was built from and needs no agreement about its wording. No
 * arithmetic: the unit chooses between two `Intl` field sets, exactly as
 * `formatSessionDate` chooses one, and local midnight is used for the same
 * reason it is there — a date-only string is a calendar day and has no instant
 * to convert.
 */
export function formatPeriodName(
  date: string | null | undefined,
  unit: 'week' | 'month',
): string {
  if (!date) return '—'
  const parts = calendarDateParts(date)
  if (parts === null) return date
  const d = new Date(parts.year, parts.month - 1, parts.day)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(
    undefined,
    unit === 'week' ? { day: 'numeric', month: 'short' } : { month: 'long' },
  )
}

/**
 * The year/month/day a `YYYY-MM-DD` string names, or null if it is not one.
 *
 * Exported so the rule can be tested **without depending on where the test runs**.
 * The whole defect is invisible in UTC and in every European zone, which is where
 * this suite runs and where it ran for the whole of Sprint 1.8 — so a test that
 * only compares rendered strings would be green on the machine that has the bug.
 */
export function calendarDateParts(
  date: string,
): { year: number; month: number; day: number } | null {
  const parts = CALENDAR_DATE.exec(date)
  if (parts === null) return null
  return { year: Number(parts[1]), month: Number(parts[2]), day: Number(parts[3]) }
}

/** "3 days ago" — for a queue where the wait is the point. */
export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const seconds = Math.round((then - now) / 1000)
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000], ['month', 2_592_000], ['week', 604_800],
    ['day', 86_400], ['hour', 3_600], ['minute', 60],
  ]
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit)
  }
  return rtf.format(seconds, 'second')
}

/**
 * A score, as a person can read it.
 *
 * ── WHY THIS IS PRESENTATION AND NOT THE CLIENT DECIDING ────────────────────
 *
 * The canonical payload carries `signal_quality: 96.50460311338604`. Rendering
 * that verbatim is not "the value as given" — it is an artefact of how a float
 * serialises, and it communicates precision the headset does not have. The
 * Status Guide calls these "a simple 0–100 score". One decimal place is the
 * reading; the sixteen digits behind it are noise that looks like data, which is
 * the overclaim §Notes warns about in the one place a user sees a number.
 *
 * The spec permits this explicitly: "Ordinary navigation/form copy, date
 * formatting, and chart positioning are client presentation; they must not
 * change the meaning of a measurement." 96.5 and 96.50460311338604 are the same
 * measurement. What is forbidden — and is not done here — is a threshold, a
 * rescaling, a band, or a label.
 *
 * It is also the difference between fitting on a phone and not. At 360 px the
 * raw string renders 319 px wide inside a 328 px card, with its right edge
 * exactly on the viewport boundary: nine pixels from the page-level sideways
 * scroll §7.3 forbids, on a value nothing in the client controls.
 *
 * `src/lib/dailyStatus.ts` still carries the raw number. The read model does no
 * arithmetic; this is the view layer, and it is the only place a score is
 * turned into characters.
 */
export function formatScore(value: number): string {
  if (!Number.isFinite(value)) return '—'
  // `String(Number)` drops a trailing `.0`, so 50 stays "50" and does not become
  // "50.0" — a decimal place that is always zero reads as precision too.
  return String(Math.round(value * 10) / 10)
}

/**
 * A confidence, as every screen writes it (UI-QA Q8): ONE formatter, so Home,
 * Report, History and Recording detail cannot disagree. `formatScore`'s rule —
 * at most one decimal place, no trailing `.0` — on the payload's own scale: no
 * percent sign, no rescaling, no clamp. `0` stays `0`. `null` or a non-finite
 * value is `null`, which the caller writes as unavailable, never as zero.
 *
 * Display only. "Held low" and every other decision read the stored number
 * (`low_confidence_flag`), never this string.
 */
export function formatConfidence(value: number | null | undefined): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null
  return formatScore(value)
}

/**
 * A signed integer percent, as the Alpha reactivity card writes it (Sprint
 * 1.9.13 W4): `+38 %`, `−12 %`, `0 %`. The sign is `Intl`'s, so nothing here
 * compares the value with zero to decide it; the value is eeg-tracking's
 * `value_pct`, already rounded by the package. `null` or a non-finite value is
 * `null`, which the caller writes as unavailable, never as zero.
 */
const SIGNED_PERCENT = new Intl.NumberFormat('en-GB', { signDisplay: 'exceptZero', maximumFractionDigits: 0 })
export function formatSignedPercent(value: number | null | undefined): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null
  return `${SIGNED_PERCENT.format(value)} %`
}

/* ── Relative days (Sprint 1.9.6 D1) ─────────────────────────────────────────
 *
 * R30 struck "N days ago" from Home because no formatter existed; D1 reverses
 * it, app-wide, and this is the formatter. Relative time is presentation, not a
 * measurement: it restates a date the payload or the list already carries, in
 * the reader's own calendar. Absolute dates stay the default wherever a date
 * IDENTIFIES a record (History's table, Recording detail's h1) — this is for
 * sentences, not for keys.
 */

const MS_PER_DAY = 86_400_000

/** How many days, counting today as 0, are said relatively. From 7 on, the date. */
const RELATIVE_DAYS = 7

type CalendarDay = { year: number; month: number; day: number }

/**
 * The LOCAL calendar day a value names: a `YYYY-MM-DD` date as written, or an
 * instant as the day it fell on in the reader's zone. Null for anything else.
 *
 * An instant is never cut with `toISOString().slice(0, 10)`: that is the UTC
 * day, and at 22:00 in New York it is already tomorrow.
 */
function localDay(value: string): CalendarDay | null {
  const calendar = calendarDateParts(value)
  if (calendar !== null) {
    // `calendarDateParts` checks the shape, not the calendar, and `Date.UTC`
    // rolls "2026-13-40" over into February. A day that does not survive the
    // round trip is not a day.
    const back = new Date(Date.UTC(calendar.year, calendar.month - 1, calendar.day))
    const real = back.getUTCFullYear() === calendar.year
      && back.getUTCMonth() === calendar.month - 1
      && back.getUTCDate() === calendar.day
    return real ? calendar : null
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() }
}

/**
 * A day as an integer, so two days subtract exactly.
 *
 * `Date.UTC` of the calendar parts, not the difference of two local instants:
 * a local day is 23 or 25 hours long across a DST change, so `floor()` of an
 * elapsed-milliseconds difference is off by one on exactly those days, in one
 * direction or the other. `trend.ts` counts days the same way.
 */
function dayNumber({ year, month, day }: CalendarDay): number {
  return Date.UTC(year, month - 1, day) / MS_PER_DAY
}

const todayOf = (now: Date): CalendarDay => ({
  year: now.getFullYear(),
  month: now.getMonth() + 1,
  day: now.getDate(),
})

/** `YYYY-MM-DD` for a local calendar day. */
function isoDay({ year, month, day }: CalendarDay): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * "today", "yesterday", "3 days ago" — or the date itself.
 *
 * `value` is a `session_date` (a calendar date) or an instant such as
 * `created_at`; `now` is REQUIRED, so a screen captures its own "today" once
 * rather than asking the clock again on every render.
 *
 *  - 0 to 6 days back: relative, in the reader's own language, by
 *    `Intl.RelativeTimeFormat` — no English is authored here.
 *  - 7 days or more: the absolute date, because "23 days ago" asks the reader
 *    to do the arithmetic the date would have saved them.
 *  - A day after today: the absolute date, never "in 2 days". A recording
 *    cannot be made in the future, so a future date is a clock that disagrees
 *    with ours, and a relative phrase would state the disagreement as a fact.
 *    Minutes of skew within the same day still read "today".
 *  - Nothing, or something that is not a date: `null`, so the caller renders
 *    nothing rather than a dash inside a sentence.
 */
export function formatRelativeDay(value: string | null | undefined, now: Date): string | null {
  if (!value) return null
  const day = localDay(value)
  if (day === null) return null
  const back = dayNumber(todayOf(now)) - dayNumber(day)
  if (back < 0 || back >= RELATIVE_DAYS) {
    return calendarDateParts(value) !== null ? formatSessionDate(value) : formatDate(value)
  }
  return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(-back, 'day')
}

/** "Saturday, September 12, 2026" — a day named in full, for an eyebrow. */
export function formatWeekdayDate(now: Date): string {
  return now.toLocaleDateString(undefined, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  })
}

/**
 * The last seven local calendar days, today included, as the bounds a
 * `session_date` is compared against — plus the instant a page of uploads
 * has to reach back past for a count over those days to be the whole count.
 *
 * `since` and `today` are `YYYY-MM-DD`, so they compare with a `session_date`
 * as text. `coveredBefore` is local midnight TWO days before `since`: a session
 * is dated in the zone it was recorded in, and UTC offsets span 26 hours
 * (−12 to +14), so an upload of a recording dated `since` can carry a
 * `created_at` up to a day and two hours before our own midnight on `since`.
 * Every upload is after its recording, so a page whose oldest upload is earlier
 * than this holds every recording dated inside the window.
 */
export function lastSevenDays(now: Date): { since: string; today: string; coveredBefore: string } {
  const y = now.getFullYear()
  const m = now.getMonth()
  const d = now.getDate()
  const back = RELATIVE_DAYS - 1
  const since = new Date(y, m, d - back)
  return {
    since: isoDay(todayOf(since)),
    today: isoDay(todayOf(now)),
    coveredBefore: new Date(y, m, d - back - 2).toISOString(),
  }
}

/* ── Home v1, the mirror (Sprint 1.9.9) ──────────────────────────────────────
 *
 * Three small formatters for the mirror's chrome. None of them reads a
 * measurement: a length of time the capture flow fixes, a weekday name, and
 * the local calendar day.
 */

/**
 * "3 min" — a whole number of minutes, for the one Record action's label.
 *
 * `seconds` is `RECORD_SECONDS` (`lib/capture/quality.ts`), so the label
 * follows the recording's real length rather than restating it (1.9.9 D6).
 */
export function formatMinutes(seconds: number): string {
  return `${Math.round(seconds / 60)} min`
}

/**
 * "Thu" — the short weekday a `YYYY-MM-DD` date names, for the week's dots.
 *
 * Built from the calendar parts at UTC midnight and formatted IN UTC, so the
 * weekday is the one the string names in every zone. Formatting the same
 * instant in the reader's zone would name the day before it everywhere west
 * of UTC, which is the `formatSessionDate` trap one field over. Null for
 * anything that is not a calendar date.
 */
export function formatWeekdayShort(date: string): string | null {
  const day = calendarDateParts(date)
  if (day === null) return null
  const at = new Date(Date.UTC(day.year, day.month - 1, day.day))
  if (Number.isNaN(at.getTime())) return null
  return at.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' })
}

/** `YYYY-MM-DD` for the local calendar day `now` falls on — never the UTC one. */
export function localIsoDay(now: Date): string {
  return isoDay(todayOf(now))
}

/**
 * Local noon on the calendar day a `YYYY-MM-DD` string names, or null.
 *
 * The mirror says "yesterday" about a reading relative to `/home.today`, the
 * day the SERVER resolved in the browser's zone, not relative to the page's
 * own clock: the dimming and its label then read the same "today" (critique
 * #20). Noon, so a DST change on that day cannot push it across midnight.
 */
export function localNoonOf(date: string): Date | null {
  const day = localDay(date)
  if (day === null || calendarDateParts(date) === null) return null
  return new Date(day.year, day.month - 1, day.day, 12)
}

/**
 * "+1.0", "-0.9", "0.0" — a state-map axis value, for `/state`'s rows and table
 * (Sprint 1.9.9 S6).
 *
 * The axes are z against the person's own baseline, signed, so the sign is
 * written out: a bare "0.9" beside "-0.9" reads as a magnitude. One decimal,
 * for `formatScore`'s reason — the digits behind it are float noise — and
 * `signDisplay: 'exceptZero'` rather than a comparison, so a value that rounds
 * to zero is "0.0" and no branch here decides which side of zero anything is.
 * The number is the payload's; this only turns it into characters.
 */
const AXIS_VALUE = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: 'exceptZero',
})

export function formatAxisValue(value: number): string {
  return Number.isFinite(value) ? AXIS_VALUE.format(value) : '—'
}

/**
 * An axis value to two places with its sign, as the approved State prototype
 * writes one ("+0.60", "-1.40", "0.00"; R72). The number is the payload's.
 */
const AXIS_VALUE_2 = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  signDisplay: 'exceptZero',
})

export function formatAxisValue2(value: number): string {
  return Number.isFinite(value) ? AXIS_VALUE_2.format(value) : '—'
}

/**
 * A recording date in the State prototype's words (R72): "Saturday, 12
 * September", "Sat", "12", "12 Sep". The date is the recording's calendar
 * date, read in UTC so no zone moves it to a neighbouring day.
 */
export interface StateDateWords {
  full: string
  weekdayShort: string
  day: string
  month: string
  short: string
  long: string
  year: string
}

const UTC_PART = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' })
const WEEKDAY = UTC_PART({ weekday: 'long' })
const WEEKDAY_SHORT = UTC_PART({ weekday: 'short' })
const MONTH = UTC_PART({ month: 'long' })

export function stateDateWords(sessionDate: string): StateDateWords {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(sessionDate)
  if (match === null) {
    return { full: sessionDate, weekdayShort: '', day: sessionDate, month: '', short: sessionDate, long: sessionDate, year: '' }
  }
  const at = new Date(`${sessionDate}T00:00:00Z`)
  const day = String(Number(match[3]))
  const month = MONTH.format(at)
  return {
    full: `${WEEKDAY.format(at)}, ${day} ${month}`,
    weekdayShort: WEEKDAY_SHORT.format(at),
    day,
    month,
    // The first three letters, as the prototype writes them ("Sep"; en-GB's own short month is "Sept").
    short: `${day} ${month.slice(0, 3)}`,
    long: `${day} ${month}`,
    year: match[1],
  }
}

/** The loaded dates' span, as the prototype writes it: "6–12 September", or "30 August – 12 September". */
export function stateDateRange(first: string, last: string): string {
  const a = stateDateWords(first)
  const b = stateDateWords(last)
  if (first === last) return a.long
  if (a.year !== b.year) return `${a.long} ${a.year} – ${b.long} ${b.year}`
  if (a.month === b.month) return `${a.day}–${b.day} ${b.month}`
  return `${a.long} – ${b.long}`
}

/**
 * "a moment", "30 seconds", "2 minutes": a server's `Retry-After`, in words
 * (agent-1-web, the assistant's refusal notices). Whole minutes round up, so the
 * wait named is never shorter than the wait asked for.
 */
export function formatWait(seconds: number): string {
  if (seconds <= 1) return 'a moment'
  if (seconds < 60) return `${seconds} seconds`
  const minutes = Math.ceil(seconds / 60)
  return minutes === 1 ? 'a minute' : `${minutes} minutes`
}

/** The epoch-ms instant `seconds` after `now`: when a `Retry-After` wait ends. */
export function instantAfter(seconds: number, now: number): number {
  return now + seconds * 1000
}

/** Whole seconds from `now` until the epoch-ms `at`, never negative (a wait's countdown). */
export function secondsUntil(at: number, now: number): number {
  return Math.max(0, Math.ceil((at - now) / 1000))
}

/** A recorder-local wall clock is already local; never turn it into a UTC instant. */
export function formatLocalClock(time: string | null | undefined): string {
  return time != null && /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?$/.test(time)
    ? time.slice(0, 5) : '—'
}

/** Board date, from calendar parts, with no reader-zone conversion. */
export function experimentDate(date: string): string {
  const words = stateDateWords(date)
  return `${words.weekdayShort} ${words.short}`.trim()
}

/** Measured capture length only, from signal rows at the archive's fixed 250 Hz. */
export function recordingLength(rows: number | null | undefined): string | null {
  if (rows == null || !Number.isFinite(rows)) return null
  const seconds = rows / 250
  return seconds % 60 === 0 ? `${seconds / 60} min` : `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(seconds)} s`
}

/** Signed presentation of a supplied delta; no score subtraction or judgement. */
const COMPARISON_NUMBER = new Intl.NumberFormat('en-GB', { maximumSignificantDigits: 6 })
const COMPARISON_SIGNED = new Intl.NumberFormat('en-GB', { maximumSignificantDigits: 6, signDisplay: 'exceptZero' })
export function comparisonNumber(value: number | null | undefined, signed = false): string {
  if (value == null || !Number.isFinite(value)) return 'not available'
  return (signed ? COMPARISON_SIGNED : COMPARISON_NUMBER).format(value)
}
